import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate, useParams } from 'react-router-dom';
import { useAttemptReview } from '../../../../hooks/useMoodleQuiz';
import QuestionHTML from './QuestionHTML';
import StbLoader from '../../../security-toolbox-ui/StbLoader';
import { useOptionalMoodleNavigation } from '../useOptionalMoodleNavigation';
import { buildCourseDetailPath } from '../../../../support/moodleDetailRoutes';

type ReviewInstance = Parameters<typeof useAttemptReview>[3];

interface Props {
  courseId?: string;
  quizId?: string;
  attemptId?: string;
  instance?: ReviewInstance;
  onCompleteReview?: () => void;
}

const SafeLoader: React.FC<{ isLoading: boolean; message?: string }> = ({ isLoading, message }) => {
  if (!isLoading) return null;
  try {
    return <StbLoader isLoading={true} />;
  } catch {
    return <div>{message || 'Loading...'}</div>;
  }
};

export default function MoodleQuizReview(props: Props) {
  const params = useParams<{ attemptId: string; quizId: string; courseId: string }>();
  const navigate = useNavigate();
  const nav = useOptionalMoodleNavigation();

  const attemptId = props.attemptId ?? params.attemptId;
  const quizId = props.quizId ?? params.quizId;
  const courseId = props.courseId ?? params.courseId;

  const aid = attemptId ? Number(attemptId) : undefined;
  const qid = quizId ? Number(quizId) : undefined;
  const cid = courseId ? Number(courseId) : undefined;

  const resolvedInstance = props.instance;

  const {
    data: review,
    isLoading: loadingReview,
    error: reviewError,
  } = useAttemptReview(aid, undefined, { enabled: !!aid }, resolvedInstance);

  const [showIncompleteModal, setShowIncompleteModal] = useState(false);

  useEffect(() => {
    if (review && review.errorcode && review.errorcode !== 'attemptalreadyclosed') {
      setShowIncompleteModal(true);
    }
  }, [review]);

  useEffect(() => {
    if (reviewError) {
      setShowIncompleteModal(true);
    }
  }, [reviewError]);

  // Without the Moodle navigation context there is no in-place quiz view to return
  // to, so fall back to the course level detail route.
  const courseDetailPath = buildCourseDetailPath(cid, { instance: resolvedInstance });

  const goBackToQuiz = () => {
    if (cid && qid) {
      if (nav) nav.openQuizEntry(cid, qid);
      else if (courseDetailPath) navigate(courseDetailPath);
    }
  };

  const goBackToCourse = () => {
    if (props.onCompleteReview) {
      props.onCompleteReview();
      return;
    }

    if (cid) {
      if (nav) nav.openCourse(cid);
      else if (courseDetailPath) navigate(courseDetailPath);
    }
  };

  if (!aid || !qid || !cid || isNaN(aid) || isNaN(qid) || isNaN(cid)) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h3>Invalid Review</h3>
        <p>Unable to load quiz review due to invalid parameters.</p>
      </div>
    );
  }

  if (loadingReview) {
    return <SafeLoader isLoading={true} message="Loading your quiz review..." />;
  }

  if (showIncompleteModal) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}
      >
        <div
          style={{
            background: '#fff',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '450px',
            textAlign: 'center',
          }}
        >
          <h3>Attempt Not Complete</h3>
          <p>Your quiz attempt has not been finished. Please complete the attempt before reviewing.</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button className="btn btn-outline" onClick={goBackToCourse}>
              Back to Course
            </button>
            <button className="btn btn-primary" onClick={goBackToQuiz}>
              Back to Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--hbr-spacing-md)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hbr-spacing-sm)',
          marginBottom: 'var(--hbr-spacing-lg)',
          padding: '0 0 1rem 0',
          borderBottom: '2px solid #e5e7eb',
        }}
      >
        <button
          type="button"
          className="btn btn-outline"
          onClick={goBackToCourse}
          style={{ marginRight: 'auto' }}
        >
          ← Back to Course
        </button>
        <div style={{ textAlign: 'center', flex: '1' }}>
          <h2 style={{ margin: 0, color: '#1f2937' }}>Quiz Review</h2>
          <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
            Attempt #{aid} - Review your answers and feedback
          </p>
        </div>
        <div style={{ width: '120px' }} />
      </div>

      {Array.isArray(review?.questions) && review.questions.length > 0 ? (
        <div style={{ display: 'grid', gap: 'var(--hbr-spacing-lg)' }}>
          {review.questions.map((rq: any, i: number) => {
            const key = rq?.slot ?? rq?.id ?? i;
            const hasCorrectAnswer = rq?.rightanswer || rq?.correctanswer;

            const rawUserScore = rq?.mark ?? rq?.grade;
            const rawMaxScore = rq?.maxmark ?? rq?.maxgrade;

            const hasExplicitScoring =
              rawUserScore !== undefined &&
              rawUserScore !== null &&
              rawMaxScore !== undefined &&
              rawMaxScore !== null &&
              Number.isFinite(Number(rawUserScore)) &&
              Number.isFinite(Number(rawMaxScore));

            const userScore = hasExplicitScoring ? Number(rawUserScore) : null;
            const maxScore = hasExplicitScoring ? Number(rawMaxScore) : null;

            const isFullyCorrect =
              hasExplicitScoring &&
              userScore !== null &&
              maxScore !== null &&
              maxScore > 0 &&
              userScore >= maxScore;

            const isPartiallyCorrect =
              hasExplicitScoring &&
              userScore !== null &&
              maxScore !== null &&
              maxScore > 0 &&
              userScore > 0 &&
              userScore < maxScore;

            const borderColor = !hasExplicitScoring
              ? '#d1d5db'
              : isFullyCorrect
                ? '#10b981'
                : isPartiallyCorrect
                  ? '#f59e0b'
                  : '#ef4444';

            const headerBg = !hasExplicitScoring
              ? '#f9fafb'
              : isFullyCorrect
                ? '#ecfdf5'
                : isPartiallyCorrect
                  ? '#fffbeb'
                  : '#fef2f2';

            const titleColor = !hasExplicitScoring
              ? '#374151'
              : isFullyCorrect
                ? '#047857'
                : isPartiallyCorrect
                  ? '#b45309'
                  : '#dc2626';

            const badgeBg = !hasExplicitScoring
              ? '#6b7280'
              : isFullyCorrect
                ? '#10b981'
                : isPartiallyCorrect
                  ? '#f59e0b'
                  : '#ef4444';

            const statusIcon = !hasExplicitScoring
              ? '📝'
              : isFullyCorrect
                ? '✅'
                : isPartiallyCorrect
                  ? '🟡'
                  : '❌';

            return (
              <div
                key={key}
                style={{
                  background: '#fff',
                  border: `2px solid ${borderColor}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <div
                  style={{
                    background: headerBg,
                    padding: '1rem 1.25rem',
                    borderBottom: `1px solid ${borderColor}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <h4
                    style={{
                      margin: 0,
                      color: titleColor,
                      fontSize: '1.1rem',
                    }}
                  >
                    Question {i + 1} {statusIcon}
                  </h4>
                  <div
                    style={{
                      background: badgeBg,
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                    }}
                  >
                    {hasExplicitScoring ? `${userScore} / ${maxScore} points` : 'Review available'}
                  </div>
                </div>

                <div style={{ padding: '1.25rem' }}>
                  <QuestionHTML html={(rq?.html ?? rq?.questiontext ?? '') as string} />

                  {(rq?.feedback || rq?.generalfeedback || hasCorrectAnswer) && (
                    <div
                      style={{
                        marginTop: '1.5rem',
                        padding: '1rem',
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                      }}
                    >
                      <h5
                        style={{
                          margin: '0 0 0.75rem 0',
                          color: '#495057',
                          fontSize: '1rem',
                          fontWeight: '600',
                        }}
                      >
                        Feedback
                      </h5>

                      {rq?.feedback && (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <strong>Response Feedback:</strong>
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rq.feedback) }} />
                        </div>
                      )}

                      {rq?.generalfeedback && (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <strong>General Feedback:</strong>
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rq.generalfeedback) }} />
                        </div>
                      )}

                      {hasCorrectAnswer && (
                        <div>
                          <strong>Correct Answer:</strong>
                          <div
                            style={{
                              padding: '0.5rem',
                              background: '#d4edda',
                              border: '1px solid #c3e6cb',
                              borderRadius: '4px',
                              marginTop: '0.5rem',
                            }}
                          >
                            {rq.rightanswer || rq.correctanswer}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : review && review.html ? (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            padding: '1.25rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(review.html) }} />
        </div>
      ) : (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <h4>No Review Available</h4>
          <p>No questions found in this review.</p>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 'var(--hbr-spacing-lg)',
          padding: '1.5rem 0 0 0',
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <button type="button" className="btn btn-outline" onClick={goBackToQuiz}>
          ← Back to Quiz
        </button>

        <button type="button" className="btn btn-primary" onClick={goBackToCourse}>
          Complete Review →
        </button>
      </div>
    </div>
  );
}